import { useMemo, useState } from 'react';
import { Row, Col, Card, Form, Button, Badge, Modal, Alert, Spinner, Table } from 'react-bootstrap';
import SearchBox from '../../components/common/SearchBox';
import {
  useMaterialSpecs,
  useCreateMaterialSpec,
  useUpdateMaterialSpec,
  useDeleteMaterialSpec,
  useMergeMaterialSpec
} from '../../features/material-specs/hooks/useMaterialSpecs';

// The places a material can appear in a standard. Same strings the extractor
// records, so the picker in the wizard can filter on them directly.
const USED_IN = [
  { value: '', label: 'All phases' },
  { value: 'conductor_isolation.isolation', label: 'Insulation' },
  { value: 'conductor_isolation.wire', label: 'Conductor wire' },
  { value: 'pre_stranding', label: 'Stranding' },
  { value: 'pre_stranding.rope', label: 'Stranding — rope' },
  { value: 'interior_sheat', label: 'Inner sheath' },
  { value: 'exterior_sheat', label: 'Outer sheath' },
  { value: 'armouring', label: 'Armouring' }
];

const USED_IN_LABEL = Object.fromEntries(USED_IN.map(u => [u.value, u.label]));

const colorDot = color => {
  const map = {
    BLUE: '#1976d2',
    BROWN: '#795548',
    'GREEN-YELLOW': '#9acd32',
    BLACK: '#000000',
    GREY: '#9e9e9e',
    RED: '#e53935'
  };
  return map[String(color || '').toUpperCase()] || '#cccccc';
};

const emptyDraft = {
  material: '',
  type: '',
  color: '',
  density: '',
  usedIn: [],
  notes: '',
  active: true
};

const SpecModal = ({ show, onHide, spec, onSave, busy, error }) => {
  const [draft, setDraft] = useState(emptyDraft);
  const isNew = !spec?._id;

  // Reset whenever a different row is opened.
  const key = spec?._id || 'new';
  const [lastKey, setLastKey] = useState(key);
  if (key !== lastKey) {
    setLastKey(key);
    setDraft(
      spec
        ? {
            material: spec.material || '',
            type: spec.type || '',
            color: spec.color || '',
            density: spec.density ?? '',
            usedIn: spec.usedIn || [],
            notes: spec.notes || '',
            active: spec.active !== false
          }
        : emptyDraft
    );
  }

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-7">{isNew ? 'New material' : 'Edit material'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="fs-9">{error}</Alert>}

        {!isNew && spec?.source === 'imported' && (
          <Alert variant="info" className="fs-10 py-2">
            Derived from {spec.standardsCount} existing standard(s). Changing the density here
            affects the <strong>next</strong> standard built from it — standards already written
            keep the number they were made with.
          </Alert>
        )}

        <Row className="g-2">
          <Col md={6}>
            <Form.Label className="fs-9 fw-semibold">Material</Form.Label>
            <Form.Control
              size="sm"
              value={draft.material}
              disabled={!isNew}
              placeholder="PVC, POLIETILEN, XLPE…"
              onChange={e => set('material', e.target.value.toUpperCase())}
            />
          </Col>
          <Col md={6}>
            <Form.Label className="fs-9 fw-semibold">Type</Form.Label>
            <Form.Control size="sm" value={draft.type} onChange={e => set('type', e.target.value)} />
          </Col>
          <Col md={6}>
            <Form.Label className="fs-9 fw-semibold">Colour</Form.Label>
            <Form.Control
              size="sm"
              value={draft.color}
              placeholder="only for insulation"
              onChange={e => set('color', e.target.value.toUpperCase())}
            />
          </Col>
          <Col md={6}>
            <Form.Label className="fs-9 fw-semibold">Density (g/cm³)</Form.Label>
            <Form.Control
              size="sm"
              type="number"
              step="0.001"
              value={draft.density}
              onChange={e => set('density', e.target.value)}
            />
          </Col>
          <Col xs={12}>
            <Form.Label className="fs-9 fw-semibold mt-2">Used in</Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {USED_IN.filter(u => u.value).map(u => (
                <Form.Check
                  key={u.value}
                  type="checkbox"
                  id={`usedin-${u.value}`}
                  className="fs-9"
                  label={u.label}
                  checked={draft.usedIn.includes(u.value)}
                  onChange={e =>
                    set(
                      'usedIn',
                      e.target.checked
                        ? [...draft.usedIn, u.value]
                        : draft.usedIn.filter(v => v !== u.value)
                    )
                  }
                />
              ))}
            </div>
          </Col>
          <Col xs={12}>
            <Form.Label className="fs-9 fw-semibold mt-2">Notes</Form.Label>
            <Form.Control size="sm" value={draft.notes} onChange={e => set('notes', e.target.value)} />
          </Col>
          <Col xs={12}>
            <Form.Check
              type="switch"
              className="fs-9 mt-2"
              label={draft.active ? 'Offered in the wizard' : 'Hidden from the wizard'}
              checked={draft.active}
              onChange={e => set('active', e.target.checked)}
            />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={onHide} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={busy || !draft.material.trim()}
          onClick={() => onSave({ ...draft, density: draft.density === '' ? null : Number(draft.density) })}
        >
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const MergeModal = ({ show, onHide, spec, candidates, onMerge, busy }) => {
  const [intoId, setIntoId] = useState('');
  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-7">Merge material</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="fs-9 text-700">
          <strong>
            {spec?.material} {spec?.type} {spec?.color}
          </strong>{' '}
          is hidden from the wizard and its spellings move to the material you pick. Standards
          already built on it are not touched.
        </p>
        <Form.Select size="sm" value={intoId} onChange={e => setIntoId(e.target.value)}>
          <option value="">Merge into…</option>
          {candidates.map(c => (
            <option key={c._id} value={c._id}>
              {[c.material, c.type, c.color].filter(Boolean).join(' ')} · {c.density ?? '—'} ·{' '}
              {c.standardsCount} uses
            </option>
          ))}
        </Form.Select>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={onHide} disabled={busy}>
          Cancel
        </Button>
        <Button variant="warning" disabled={!intoId || busy} onClick={() => onMerge(intoId)}>
          {busy ? 'Merging…' : 'Merge'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const MaterialCatalogue = () => {
  const [usedIn, setUsedIn] = useState('');
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState(null); // spec | {} for new
  const [merging, setMerging] = useState(null);
  const [error, setError] = useState(null);

  const { data: specs = [], isLoading } = useMaterialSpecs({ usedIn, includeInactive });
  const create = useCreateMaterialSpec();
  const update = useUpdateMaterialSpec();
  const remove = useDeleteMaterialSpec();
  const merge = useMergeMaterialSpec();

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return specs;
    return specs.filter(s =>
      [s.material, s.type, s.color, s.notes]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(q))
    );
  }, [specs, search]);

  const conflicts = rows.filter(s => s.densityConflict);

  const save = async draft => {
    setError(null);
    try {
      if (editing?._id) await update.mutateAsync({ id: editing._id, ...draft });
      else await create.mutateAsync(draft);
      setEditing(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    }
  };

  const busy = create.isPending || update.isPending || remove.isPending || merge.isPending;

  return (
    <>
      <Row className="gy-3 mb-4 align-items-center justify-content-between">
        <Col xl={7}>
          <h2 className="mb-2 text-1100">Material catalogue</h2>
          <h5 className="text-700 fw-semi-bold mb-0">
            What a standard can be built from — one entry per compound, so the same material stops
            being retyped four different ways
          </h5>
        </Col>
        <Col xl="auto">
          <Button variant="primary" onClick={() => setEditing({})}>
            New material
          </Button>
        </Col>
      </Row>

      {conflicts.length > 0 && (
        <Alert variant="warning" className="fs-9">
          <strong>{conflicts.length}</strong> material(s) are recorded at more than one density in
          the existing standards. Density feeds straight into the kilograms on every job, so these
          were <em>not</em> merged automatically — open each one and hide whichever is the mistake.
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
            <SearchBox
              placeholder="Search material, type, colour…"
              onChange={e => setSearch(e.target.value)}
            />
            <Form.Select
              size="sm"
              style={{ width: 'auto' }}
              value={usedIn}
              onChange={e => setUsedIn(e.target.value)}
            >
              {USED_IN.map(u => (
                <option key={u.value || 'all'} value={u.value}>
                  {u.label}
                </option>
              ))}
            </Form.Select>
            <Form.Check
              type="switch"
              className="fs-9 ms-2"
              label="Show hidden"
              checked={includeInactive}
              onChange={e => setIncludeInactive(e.target.checked)}
            />
            <span className="ms-auto text-700 fs-9">{rows.length} material(s)</span>
          </div>

          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading catalogue…
            </div>
          ) : (
            <div className="table-responsive">
              <Table size="sm" className="fs-9 align-middle mb-0">
                <thead>
                  <tr className="text-700 text-uppercase fs-10">
                    <th>Material</th>
                    <th>Type</th>
                    <th>Colour</th>
                    <th className="text-end">Density</th>
                    <th className="text-end">Uses</th>
                    <th>Used in</th>
                    <th>Notes</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map(s => (
                    <tr key={s._id} className={s.active === false ? 'opacity-50' : undefined}>
                      <td className="fw-semibold">{s.material}</td>
                      <td>
                        {s.type || '—'}
                        {!!s.aliases?.length && (
                          <div className="text-700 fs-10">
                            also: {s.aliases.map(a => `"${a.type}"`).join(', ')}
                          </div>
                        )}
                      </td>
                      <td>
                        {s.color ? (
                          <>
                            <span
                              className="d-inline-block rounded-circle me-1"
                              style={{
                                width: 9,
                                height: 9,
                                background: colorDot(s.color),
                                border: '1px solid rgba(0,0,0,.25)'
                              }}
                            />
                            {s.color}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="text-end">
                        {s.density ?? <span className="text-danger">missing</span>}
                        {s.densityConflict && (
                          <Badge bg="warning" className="ms-2 fs-10">
                            conflict
                          </Badge>
                        )}
                      </td>
                      <td className="text-end">{s.standardsCount}</td>
                      <td className="text-700 fs-10">
                        {(s.usedIn || []).map(u => USED_IN_LABEL[u] || u).join(', ') || '—'}
                      </td>
                      <td className="text-700 fs-10">{s.notes || ''}</td>
                      <td className="text-end text-nowrap">
                        <Button size="sm" variant="link" className="py-0" onClick={() => setEditing(s)}>
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="link"
                          className="py-0 text-warning"
                          onClick={() => setMerging(s)}
                        >
                          Merge
                        </Button>
                        {s.source !== 'imported' && (
                          <Button
                            size="sm"
                            variant="link"
                            className="py-0 text-danger"
                            onClick={() => remove.mutate(s._id)}
                          >
                            Delete
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!rows.length && (
                    <tr>
                      <td colSpan={8} className="text-center text-700 py-4">
                        Nothing here yet. Run{' '}
                        <code>node scripts/seed-material-specs.mjs --apply</code> to build the
                        catalogue from the standards you already have.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <SpecModal
        show={!!editing}
        spec={editing}
        onHide={() => {
          setEditing(null);
          setError(null);
        }}
        onSave={save}
        busy={busy}
        error={error}
      />

      <MergeModal
        show={!!merging}
        spec={merging}
        candidates={specs.filter(s => s._id !== merging?._id && s.material === merging?.material)}
        onHide={() => setMerging(null)}
        busy={busy}
        onMerge={async intoId => {
          await merge.mutateAsync({ id: merging._id, intoId });
          setMerging(null);
        }}
      />
    </>
  );
};

export default MaterialCatalogue;
